import { motion } from "framer-motion"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { RiArrowRightLine } from "@remixicon/react"

import { Button } from "@/components/ui/button"
import { useAppSession } from "@/hooks/useAppSession"
import { APP_REGISTER_URL } from "@/lib/site"
import { cn } from "@/lib/utils"
import {
  getValidStoreHandle,
  normalizeStoreHandle,
  persistClaimedStoreUrl,
} from "@/utils/storeUrlValidator"

import { easeOut } from "./marketing-motion"

type SalesLinkClaimProps = {
  className?: string
  /** Light text on dark backgrounds */
  onDark?: boolean
  placeholder?: string
  animate?: boolean
}

export function SalesLinkClaim({
  className,
  placeholder = "yourstore",
  animate = true,
}: SalesLinkClaimProps) {
  const { signedIn, startHref, startLabel, currentStore } = useAppSession()
  const [handle, setHandle] = useState("")

  useEffect(() => {
    if (currentStore?.url) {
      setHandle(normalizeStoreHandle(currentStore.url))
    }
  }, [currentStore?.url])

  const claimedHandle = getValidStoreHandle(handle)
  const registerUrl = claimedHandle
    ? `${APP_REGISTER_URL}?store=${encodeURIComponent(claimedHandle)}`
    : APP_REGISTER_URL
  const href = signedIn ? startHref : registerUrl
  const ctaLabel = signedIn ? startLabel : "Claim your link"

  const claimLink = () => {
    if (!signedIn && claimedHandle) persistClaimedStoreUrl(claimedHandle)
  }

  const wrapper = animate
    ? {
        initial: { opacity: 0, y: 14 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.45, ease: easeOut },
      }
    : {}

  return (
    <motion.div className={cn("w-full max-w-xl", className)} {...wrapper}>
      <div
        className={cn(
          "flex flex-col gap-2 rounded-2xl p-1.5 sm:flex-row sm:items-center sm:rounded-full bg-white",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-0 rounded-full pl-4">
          <span
            className={cn(
              "shrink-0 text-sm font-medium sm:text-base text-muted-foreground",
            )}
          >
            sqale.shop/
          </span>
          <input
            type="text"
            value={handle}
            onChange={(e) => setHandle(normalizeStoreHandle(e.target.value))}
            placeholder={placeholder}
            readOnly={signedIn}
            className={cn(
              "min-w-0 flex-1 bg-transparent py-3 text-sm font-medium outline-none sm:text-base",
              "text-foreground placeholder:text-muted-foreground",
              signedIn && "cursor-default",
            )}
            aria-label="Your sales link handle"
            autoComplete="off"
            spellCheck={false}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return
              e.preventDefault()
              claimLink()
              window.location.assign(href)
            }}
          />
        </div>
        <Button size="lg" className="rounded-full px-6" asChild>
          <Link to={href} className="gap-2" onClick={claimLink}>
            {ctaLabel}
            <RiArrowRightLine className="size-4" />
          </Link>
        </Button>
      </div>
    </motion.div>
  )
}
